import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/lib/errors';

// ─── FDI Notation ─────────────────────────────────────────────────────────────

const ADULT_TEETH = {
  upper: [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28],
  lower: [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38],
};
const PEDI_TEETH = {
  upper: [55, 54, 53, 52, 51, 61, 62, 63, 64, 65],
  lower: [85, 84, 83, 82, 81, 71, 72, 73, 74, 75],
};

function isAnterior(n: number) {
  const u = n % 10;
  return u >= 1 && u <= 3;
}

const CONDITIONS = [
  // Sano y caries
  { id: 'normal',      label: 'Sano',           color: '#e5e7eb' },
  { id: 'caries',      label: 'Caries',          color: '#ef4444' },
  { id: 'caries_sec',  label: 'Caries sec.',     color: '#991b1b' },
  // Pulpar / periapical
  { id: 'endodoncia',  label: 'Endodoncia',      color: '#8b5cf6' },
  { id: 'necrosis',    label: 'Necrosis pulpar', color: '#1f2937' },
  { id: 'absceso',     label: 'Absceso',         color: '#7f1d1d' },
  // Restauraciones y prótesis
  { id: 'obturado',    label: 'Obturación',      color: '#64748b' },
  { id: 'corona',      label: 'Corona',          color: '#f59e0b' },
  { id: 'pilar',       label: 'Pilar puente',    color: '#06b6d4' },
  { id: 'pontico',     label: 'Póntico',         color: '#0891b2' },
  { id: 'implante',    label: 'Implante',        color: '#10b981' },
  { id: 'prot_rem',    label: 'Prótesis rem.',   color: '#d97706' },
  { id: 'sellador',    label: 'Sellador',        color: '#60a5fa' },
  // Traumatismos
  { id: 'fractura',    label: 'Fractura',        color: '#f97316' },
  { id: 'extraccion',  label: 'Extracción ind.', color: '#c026d3' },
  // Desarrollo / posición
  { id: 'impactado',   label: 'Impactado',       color: '#7c3aed' },
  { id: 'hipoplasia',  label: 'Hipoplasia',      color: '#ec4899' },
  { id: 'fluorosis',   label: 'Fluorosis',       color: '#a78bfa' },
  // Desgaste / otros
  { id: 'desgaste',    label: 'Desgaste',        color: '#84cc16' },
  { id: 'movilidad',   label: 'Movilidad',       color: '#f43f5e' },
  { id: 'tincion',     label: 'Tinción',         color: '#92400e' },
];
type Condition = typeof CONDITIONS[number]['id'];

interface SurfaceMap { v: Condition; p: Condition; m: Condition; d: Condition; o: Condition }
interface ToothData {
  ausente: boolean;
  diagnostico: string;
  procedimiento: string;
  surfaces: SurfaceMap;
}

function defaultSurfaces(): SurfaceMap {
  return { v: 'normal', p: 'normal', m: 'normal', d: 'normal', o: 'normal' };
}
function defaultTooth(): ToothData {
  return { ausente: false, diagnostico: '', procedimiento: '', surfaces: defaultSurfaces() };
}

function conditionColor(id: Condition): string {
  return CONDITIONS.find(c => c.id === id)?.color ?? '#e5e7eb';
}

// ─── Small cross for tooth tile ───────────────────────────────────────────────

function ToothCross({ s }: { s: SurfaceMap }) {
  const sz = 5, gap = 1, mid = sz + gap, vb = sz * 3 + gap * 2;
  return (
    <svg width="100%" viewBox={`0 0 ${vb} ${vb}`}>
      <rect x={mid}   y={0}     width={sz} height={sz} fill={conditionColor(s.v)} rx={1} />
      <rect x={mid}   y={mid*2} width={sz} height={sz} fill={conditionColor(s.p)} rx={1} />
      <rect x={0}     y={mid}   width={sz} height={sz} fill={conditionColor(s.m)} rx={1} />
      <rect x={mid*2} y={mid}   width={sz} height={sz} fill={conditionColor(s.d)} rx={1} />
      <rect x={mid}   y={mid}   width={sz} height={sz} fill={conditionColor(s.o)} rx={1} />
    </svg>
  );
}

// ─── Large interactive cross for modal ───────────────────────────────────────

function InteractiveCross({
  s, activeCond, onSurface, isAnteriorTooth,
}: {
  s: SurfaceMap;
  activeCond: Condition | null;
  onSurface: (key: keyof SurfaceMap) => void;
  isAnteriorTooth: boolean;
}) {
  const sz = 42, gap = 6, mid = sz + gap, total = sz * 3 + gap * 2;

  const surfaces: { key: keyof SurfaceMap; x: number; y: number; abbr: string }[] = [
    { key: 'v', x: mid,   y: 0,     abbr: 'V' },
    { key: 'p', x: mid,   y: mid*2, abbr: 'P' },
    { key: 'm', x: 0,     y: mid,   abbr: 'M' },
    { key: 'd', x: mid*2, y: mid,   abbr: 'D' },
    { key: 'o', x: mid,   y: mid,   abbr: isAnteriorTooth ? 'I' : 'O' },
  ];

  return (
    <svg width={total} height={total} viewBox={`0 0 ${total} ${total}`} className="mx-auto block">
      {surfaces.map(({ key, x, y, abbr }) => {
        const col = conditionColor(s[key]);
        const dark = col !== '#e5e7eb';
        return (
          <g key={key} onClick={() => activeCond && onSurface(key)}
            style={{ cursor: activeCond ? 'pointer' : 'default' }}>
            <rect x={x} y={y} width={sz} height={sz} fill={col} rx={8}
              stroke={activeCond ? '#3b82f6' : '#d1d5db'} strokeWidth={activeCond ? 2.5 : 1} />
            <text x={x + sz / 2} y={y + sz / 2}
              textAnchor="middle" dominantBaseline="central"
              fontSize="15" fontWeight="700"
              fill={dark ? 'rgba(255,255,255,0.9)' : '#9ca3af'}
              style={{ pointerEvents: 'none', userSelect: 'none' }}>
              {abbr}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ─── Tooth modal ──────────────────────────────────────────────────────────────

function ToothModal({ number, data, onUpdate, onClose }: {
  number: number;
  data: ToothData;
  onUpdate: (fn: (prev: ToothData) => ToothData) => void;
  onClose: () => void;
}) {
  const [activeCond, setActiveCond] = useState<Condition | null>(null);
  const ant = isAnterior(number);

  function handleSurface(key: keyof SurfaceMap) {
    if (!activeCond) return;
    // Toggle: clicking the same condition again resets the surface to sano
    const next: Condition = data.surfaces[key] === activeCond ? 'normal' : activeCond;
    onUpdate(t => ({ ...t, surfaces: { ...t.surfaces, [key]: next } }));
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-y-auto max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <p className="text-[10px] text-text-secondary uppercase tracking-widest font-medium">Diente</p>
            <p className="text-2xl font-bold text-text-primary leading-tight">{number}</p>
          </div>
          <button onClick={onClose}
            className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-5">
          {/* Ausente */}
          <label className="flex items-center gap-2.5 text-sm text-text-primary font-medium cursor-pointer select-none">
            <input type="checkbox" checked={data.ausente}
              onChange={e => onUpdate(t => ({ ...t, ausente: e.target.checked }))}
              className="w-4 h-4 accent-red-500" />
            Ausente / extraído
          </label>

          {!data.ausente && (
            <>
              {/* Step 1: condition dropdown */}
              <div>
                <p className="text-[10px] font-bold text-text-secondary uppercase tracking-widest mb-2.5">
                  1 · Elige una condición
                </p>
                <div className="relative">
                  {/* color swatch preview of selected condition */}
                  <span
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 rounded flex-shrink-0 border border-black/10 pointer-events-none"
                    style={{ background: CONDITIONS.find(c => c.id === activeCond)?.color ?? '#e5e7eb' }}
                  />
                  <select
                    value={activeCond ?? ''}
                    onChange={e => setActiveCond((e.target.value || null) as Condition | null)}
                    className="w-full appearance-none border border-gray-200 rounded-xl pl-9 pr-8 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300 cursor-pointer"
                  >
                    <option value="">— Selecciona una condición —</option>
                    {CONDITIONS.map(c => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </select>
                  {/* chevron */}
                  <svg className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
              </div>

              {/* Step 2: interactive tooth cross */}
              <div>
                <div className="flex items-center justify-between mb-2.5">
                  <p className="text-[10px] font-bold text-text-secondary uppercase tracking-widest">
                    2 · Toca una superficie
                  </p>
                  <button
                    type="button"
                    disabled={!activeCond}
                    onClick={() => {
                      if (!activeCond) return;
                      onUpdate(t => ({
                        ...t,
                        surfaces: { v: activeCond, p: activeCond, m: activeCond, d: activeCond, o: activeCond },
                      }));
                    }}
                    className="flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-lg transition-all disabled:opacity-30 disabled:cursor-not-allowed bg-blue-100 text-blue-700 hover:bg-blue-200 disabled:hover:bg-blue-100"
                  >
                    <span
                      className="w-3 h-3 rounded-sm border border-black/10 flex-shrink-0"
                      style={{ background: CONDITIONS.find(c => c.id === activeCond)?.color ?? '#e5e7eb' }}
                    />
                    Aplicar a todo
                  </button>
                </div>
                <div className={cn(
                  'rounded-xl p-4 transition-colors',
                  activeCond ? 'bg-blue-50 border-2 border-blue-200' : 'bg-gray-50 border-2 border-gray-100'
                )}>
                  <InteractiveCross
                    s={data.surfaces}
                    activeCond={activeCond}
                    onSurface={handleSurface}
                    isAnteriorTooth={ant}
                  />
                  <p className={cn(
                    'text-xs text-center mt-2.5 font-medium',
                    activeCond ? 'text-blue-600' : 'text-text-secondary'
                  )}>
                    {activeCond
                      ? `Aplicando: ${CONDITIONS.find(c => c.id === activeCond)?.label}`
                      : 'Selecciona una condición primero'}
                  </p>
                </div>
                <div className="flex flex-wrap justify-center gap-x-3 gap-y-0.5 text-[10px] text-text-secondary mt-2">
                  <span>V=Vestibular</span>
                  <span>P=Palatino</span>
                  <span>M=Mesial</span>
                  <span>D=Distal</span>
                  <span>{ant ? 'I=Incisal' : 'O=Oclusal'}</span>
                </div>
              </div>

              {/* Text fields */}
              <div className="space-y-3">
                <div>
                  <label className="block text-[10px] font-bold text-text-secondary uppercase tracking-widest mb-1.5">
                    Diagnóstico
                  </label>
                  <textarea rows={2} value={data.diagnostico}
                    onChange={e => onUpdate(t => ({ ...t, diagnostico: e.target.value }))}
                    placeholder="Diagnóstico clínico..."
                    className="w-full text-sm resize-none border border-gray-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-primary-300 placeholder:text-gray-300" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-text-secondary uppercase tracking-widest mb-1.5">
                    Procedimiento
                  </label>
                  <textarea rows={2} value={data.procedimiento}
                    onChange={e => onUpdate(t => ({ ...t, procedimiento: e.target.value }))}
                    placeholder="Tratamiento indicado..."
                    className="w-full text-sm resize-none border border-gray-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-primary-300 placeholder:text-gray-300" />
                </div>
              </div>
            </>
          )}

          <button onClick={onClose}
            className="w-full py-2.5 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700 transition-colors">
            Listo
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Single tooth tile ────────────────────────────────────────────────────────

function ToothTile({ number, data, isSelected, onClick }: {
  number: number;
  data: ToothData;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick}
      className={cn(
        'flex flex-col items-center gap-0.5 px-0.5 py-1 rounded-lg transition-all flex-1 min-w-0',
        data.ausente ? 'opacity-40 bg-gray-100' : 'bg-white hover:bg-blue-50',
        isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : 'border border-gray-200 hover:border-blue-300',
      )}>
      <span className="text-[9px] font-bold text-gray-500">{number}</span>
      {data.ausente
        ? <span className="text-[9px] text-gray-400">✕</span>
        : <ToothCross s={data.surfaces} />}
    </button>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface Props { id: string; canEdit: boolean; tipo: 'adulto' | 'infantil'; consultaId?: string | null }

export function OdontogramaView({ id, canEdit, tipo, consultaId }: Props) {
  const qc = useQueryClient();
  const teeth = tipo === 'adulto' ? ADULT_TEETH : PEDI_TEETH;
  const allNumbers = [...teeth.upper, ...teeth.lower];

  const [data, setData] = useState<Record<number, ToothData>>({});
  const [dirty, setDirty] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);

  const { data: saved, isLoading } = useQuery({
    queryKey: ['odontograma', id, tipo, consultaId ?? null],
    queryFn: () => api.get(`/api/pacientes/${id}/odontograma`, { params: { tipo, consultaId: consultaId ?? undefined } }).then(r => r.data),
  });

  useEffect(() => {
    const raw: Record<number, ToothData> = {};
    if (saved?.datosJson) Object.assign(raw, saved.datosJson as Record<number, ToothData>);
    const merged: Record<number, ToothData> = {};
    for (const n of allNumbers) merged[n] = raw[n] ?? defaultTooth();
    setData(merged);
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved, tipo]);

  const saveMutation = useMutation({
    mutationFn: () => api.put(`/api/pacientes/${id}/odontograma`, { tipo, datosJson: data, consultaId: consultaId ?? undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['odontograma', id, tipo, consultaId ?? null] });
      toast.success('Odontograma guardado');
      setDirty(false);
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al guardar')),
  });

  function getTooth(n: number): ToothData {
    return data[n] ?? defaultTooth();
  }

  function updateTooth(n: number, fn: (prev: ToothData) => ToothData) {
    setData(prev => ({ ...prev, [n]: fn(prev[n] ?? defaultTooth()) }));
    setDirty(true);
  }

  if (isLoading) return <p className="text-center py-8 text-sm text-text-secondary">Cargando...</p>;

  const half = (arr: number[]) => [arr.slice(0, arr.length / 2), arr.slice(arr.length / 2)] as const;

  return (
    <div className="space-y-3">
      {/* Save button */}
      {canEdit && dirty && (
        <div className="flex justify-end">
          <button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}
            className="flex items-center gap-1.5 bg-primary-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-primary-700 disabled:opacity-60 transition-colors">
            <Save size={12} /> {saveMutation.isPending ? 'Guardando...' : 'Guardar odontograma'}
          </button>
        </div>
      )}

      {/* Dental chart */}
      <div className="bg-white border border-gray-100 rounded-2xl p-3">
        {/* Upper arch */}
        <div className="flex items-end pb-1 border-b border-dashed border-gray-200 mb-1">
          <div className="flex flex-1 gap-0.5">
            {half(teeth.upper)[0].map(n => (
              <ToothTile key={n} number={n} data={getTooth(n)} isSelected={selected === n}
                onClick={() => canEdit && setSelected(selected === n ? null : n)} />
            ))}
          </div>
          <div className="w-px self-stretch bg-gray-300 mx-1 flex-shrink-0" />
          <div className="flex flex-1 gap-0.5">
            {half(teeth.upper)[1].map(n => (
              <ToothTile key={n} number={n} data={getTooth(n)} isSelected={selected === n}
                onClick={() => canEdit && setSelected(selected === n ? null : n)} />
            ))}
          </div>
        </div>

        <div className="text-[9px] text-center text-gray-400 py-0.5">── Línea media ──</div>

        {/* Lower arch */}
        <div className="flex items-start pt-1 border-t border-dashed border-gray-200">
          <div className="flex flex-1 gap-0.5">
            {half(teeth.lower)[0].map(n => (
              <ToothTile key={n} number={n} data={getTooth(n)} isSelected={selected === n}
                onClick={() => canEdit && setSelected(selected === n ? null : n)} />
            ))}
          </div>
          <div className="w-px self-stretch bg-gray-300 mx-1 flex-shrink-0" />
          <div className="flex flex-1 gap-0.5">
            {half(teeth.lower)[1].map(n => (
              <ToothTile key={n} number={n} data={getTooth(n)} isSelected={selected === n}
                onClick={() => canEdit && setSelected(selected === n ? null : n)} />
            ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-2 px-1">
        {CONDITIONS.map(c => (
          <span key={c.id} className="flex items-center gap-1 text-[10px] text-gray-500">
            <span className="w-2.5 h-2.5 rounded-sm inline-block border border-gray-200"
              style={{ background: c.color }} />
            {c.label}
          </span>
        ))}
      </div>

      {/* Tooth modal */}
      {selected !== null && canEdit && (
        <ToothModal
          number={selected}
          data={getTooth(selected)}
          onUpdate={fn => updateTooth(selected, fn)}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
